'use client';

import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, HelpCircle, MapPin, Truck, X } from 'lucide-react';
import { Logo } from '@/components/layout/Header/Logo';
import { mobileNavData } from './data';
import styles from './MobileMenu.module.css';
import megaStyles from './MegaMenu.module.css';
import { ButtonFilled } from '@/components/ui/Button/ButtonFilled';
import { ButtonOutlined } from '@/components/ui/Button/ButtonOutlined';

interface MobileMenuOverlayProps {
    onClose: () => void;
}

interface SubmenuItem {
    title: string;
    href: string;
    description: string;
    badge?: string;
}

interface MenuItemWithSubmenu {
    title: string;
    hasSubmenu: boolean;
    submenuItems: SubmenuItem[];
}

export const MobileMenuOverlay = ({ onClose }: MobileMenuOverlayProps) => {
    const [activeSubMenu, setActiveSubMenu] = useState<MenuItemWithSubmenu | null>(null);

    const handleSubMenuOpen = (submenuData: MenuItemWithSubmenu) => {
        setActiveSubMenu(submenuData);
    };

    const handleBack = () => {
        setActiveSubMenu(null);
    };

    return (
        <div className={styles.mobileMenuOverlay}>
            <div className={styles.mobileMenuHeader}>
                {activeSubMenu ? (
                    <button onClick={handleBack} className={styles.backButton}>
                        <ChevronLeft size={32} />
                    </button>
                ) : (
                    <div className={styles.logo}>
                        <Logo />
                    </div>
                )}

                {activeSubMenu && (
                    <h2 className={styles.mobileSubMenuTitle}>{activeSubMenu.title}</h2>
                )}

                <button onClick={onClose} className={styles.closeButton}>
                    <X size={32} color="#22c55e" />
                </button>
            </div>

            <div className={styles.mobileMenuContent}>
                {!activeSubMenu ? (
                    <>
                        <div className={styles.mobileAuthButtons}>
                            <ButtonFilled full={true}>ВОЙТИ</ButtonFilled>
                            <ButtonOutlined>ОНЛАЙН-ОПЛАТА</ButtonOutlined>
                        </div>
                        <div className={styles.mobileSecondaryLinks}>
                            <span className={styles.disabledLink}>
                                <Truck size={20} />
                                <span>Отправляйте как юрлицо</span>
                            </span>
                            <span className={styles.disabledLink}>
                                <MapPin size={20} />
                                <span>Пункты CDEK</span>
                            </span>
                            <span className={styles.disabledLink}>
                                <HelpCircle size={20} />
                                <span>Помощь</span>
                            </span>
                        </div>
                        {mobileNavData.map((group) => (
                            <div key={group.title} className={styles.navGroup}>
                                <h3 className={styles.navGroupTitle}>{group.title}</h3>
                                {group.items.map((item) =>
                                    item.hasSubmenu ? (
                                        <button
                                            key={item.title}
                                            onClick={() => handleSubMenuOpen(item)}
                                            className={styles.mobileNavLink}
                                        >
                                            <span>{item.title}</span>
                                            <ChevronRight size={20} />
                                        </button>
                                    ) : item.href && item.href !== '#' ? (
                                        <a
                                            key={item.title}
                                            href={item.href}
                                            className={styles.mobileNavLink}
                                        >
                                            <span>{item.title}</span>
                                        </a>
                                    ) : (
                                        <span
                                            key={item.title}
                                            className={`${styles.mobileNavLink} ${styles.disabledItem}`}
                                            aria-disabled="true"
                                        >
                                            <span>{item.title}</span>
                                        </span>
                                    ),
                                )}
                            </div>
                        ))}
                    </>
                ) : (
                    <div className={styles.mobileSubMenu}>
                        {activeSubMenu.submenuItems.map((item) =>
                            item.href && item.href !== '#' ? (
                                <a
                                    key={item.title}
                                    href={item.href}
                                    className={styles.mobileSubMenuItem}
                                >
                                    <span className={megaStyles.megaMenuItemTitle}>
                                        {item.title}
                                        {item.badge && (
                                            <span className={megaStyles.badge}>{item.badge}</span>
                                        )}
                                    </span>
                                    <p className={megaStyles.megaMenuItemDesc}>
                                        {item.description}
                                    </p>
                                </a>
                            ) : (
                                <div
                                    key={item.title}
                                    className={`${styles.mobileSubMenuItem} ${styles.disabledItem}`}
                                    aria-disabled="true"
                                >
                                    <span className={megaStyles.megaMenuItemTitle}>
                                        {item.title}
                                        {item.badge && (
                                            <span className={megaStyles.badge}>{item.badge}</span>
                                        )}
                                    </span>
                                    <p className={megaStyles.megaMenuItemDesc}>
                                        {item.description}
                                    </p>
                                </div>
                            ),
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
